import React from 'react';

import { Logger } from '../../../Errors/Logger';
import { ProductHelper } from '../../../Helpers/ProductHelper';
import { useWindowLog } from '../../../Hooks/WindowLog';
import { DebugLineProps, DebugPointProps } from '../../../Web/Context/Debug/Commands/DebugCommands';
import { useIsMobile } from '../../../Web/Context/PlatformContext/PlatformContext';
import { useViewState } from '../../../Web/Hooks/ViewState/ViewState';
import { LaizeCalculatorResult, LaizeProps, LaizeResult, LaizeStairsCalculatorResult } from '../Components/Laizes/Laize';
import { RoomFactory } from '../Helpers/BaseRoomFactory';
import { CompleteHelper } from '../Helpers/CompleteHelper';
import { MapHelper } from '../Helpers/MapHelper';
import { MapResult } from '../Helpers/MapResult';
import { MapItemContants } from '../Models/IMapItem';
import { IOpening } from '../Models/IOpening';
import { IRoom, RoomType } from '../Models/IRoom';
import { IRoomItem, RoomItemPointMove, RoomItemWithSideIndex } from '../Models/IRoomItem';
import { Editor } from '../Utils/Editor';
import { OpeningUtils } from '../Utils/Opening/OpeningUtils';
import { qSVG } from '../Utils/qSVG';
import { RoomDiffUtils } from '../Utils/Room/RoomDiffUtils';
import { RoomMoveInit } from '../Utils/Room/RoomMove';
import { RoomUtils } from '../Utils/Room/RoomUtils';
import { RoomItemUtils } from '../Utils/RoomItem/RoomItemUtils';
import { CoordPoint, LockedWall, Wall, WallMoveInit } from '../Utils/Types';
import { WallUtils } from '../Utils/Wall/WallUtils';
import { ZoomUtils } from '../Utils/ZoomUtils';
import { EditNameProvider } from './EditNameContext';
import { GestureProvider } from './GestureContext';
import { LaizeViewProvider } from './LaizeDirectionContext';
import { MapExportProvider } from './MapExportContext';
import { MapKeybordEventsWrapper } from './MapKeybordEvents';
import { MapReadyProvider } from './MapReadyContext';
import { UndoRedoMapProvider, useUndoRedoMap } from './UndoRedoMapContext';

//#region //* STATES

/** accessible globally, not used to trigger rerender */
export const lastSnap = { coords: { x: 0, y: 0 } };

export const ClearPanelState: Partial<MapContextState> = {
    selectedRoomId: undefined,
    selectedOpeningId: undefined,
    selectedRoomItemId: undefined,
    selectAmenagementOpen: undefined,
    selectStairsOpen: undefined,
    selectRoomShapeOpen: false,
    showTutorial: false,
    showHabitation: false,
    showFinDeParcours: false,
    showStoreLocator: false,
};
export type AmenagementOpen = { roomId?: string };
export type StairsOpen = { stairsId?: string; stairsStepId?: string };
export type MapPanelsState = {
    selectedRoomId?: string; //room, wall, stair
    selectRoomShapeOpen?: boolean;
    selectAmenagementOpen?: AmenagementOpen;
    selectStairsOpen?: StairsOpen;

    selectedOpeningId?: string;
    selectedRoomItemId?: string;

    showFinDeParcours?: boolean;
    showHabitation?: boolean;
    showStoreLocator?: boolean;
};

export type MapGestureState = {
    panZoomDisabled?: boolean;

    wallMove?: Wall;
    wallMoveInit?: WallMoveInit;

    roomWallPointMove?: RoomWallPointMove;

    roomItemMoveSide?: RoomItemWithSideIndex;
    roomItemPointMove?: RoomItemPointMove;

    roomMove?: string;
    roomMoveInit?: RoomMoveInit;
    openingMoveOrPlacingInit?: IOpening;
    roomItemMoveOrPlacingInit?: IRoomItem;
};

export type RoomWallPointMove = {
    room: IRoom;
    point: CoordPoint;
    wallStartToMove: Wall;
    wallEndToMove: Wall;
    initialWallStartSize: number;
    initialWallEndSize: number;
};

export type MapContextState = {
    rooms: Array<IRoom>;
    roomsCompleted?: boolean;

    studioOpen?: boolean;

    //* from enhance
    global_surface_area?: number;

    laizeProps?: Array<LaizeProps>;
    laizeStairsProps?: Array<LaizeProps>;
    laizeResults?: Array<LaizeResult>;
    laizeStairsResults?: Array<LaizeStairsCalculatorResult>;
    laizeDebugResults?: Array<LaizeCalculatorResult>;

    showTutorial?: boolean;
    addRoomTutorialShown?: boolean;
    type?: RoomType;

    busy?: boolean;
    geometryPointsDebug?: Array<DebugPointProps>;
    geometryLinesDebug?: Array<DebugLineProps>;

    logs?: any;

    coteWallToEdit?: Wall;
    coteRoomItemToEdit?: { sideIndex: number; roomItem: IRoomItem };
    lockedWallsList?: Array<LockedWall>;
    //mapModified?: boolean;
} & MapPanelsState &
    MapGestureState;

//#endregion

type AddRoomsParams = {
    openRoomPanel?: boolean;
    mapModified?: boolean;
    fromDuplicate?: boolean;
    logs?: any;
} & Partial<MapContextState>;

export type UpdateMapParams = {
    values: Partial<MapContextState>;
    mapModified?: boolean;
    logs?: any;
    afterUpdate?: VoidFunction;
};

type StateCombiner = {
    merge: (state: Partial<MapContextState>, mapModified?: boolean) => Partial<MapContextState>;
};
type MapContextValue = {
    state: Partial<MapContextState>;
    getState: () => Partial<MapContextState>;
    resetState: (state: Partial<MapContextState>) => void;
    update: (params: UpdateMapParams) => void;
    setPanel: (params: UpdateMapParams) => void;

    addRooms: (params: AddRoomsParams) => void;
    withComputeLaize: (mapState: Partial<MapContextState>) => Partial<MapContextState>;
    zoomToFit: () => void;

    setStateCombiner: (stateCombiner: StateCombiner) => void;
};

const MapContext = React.createContext<MapContextValue>({
    state: { panZoomDisabled: true, rooms: [] },
    getState: () => ({}),
    resetState: () => ({}),
    update: () => {},
    addRooms: () => {},
    setPanel: () => {},
    withComputeLaize: () => ({}),
    zoomToFit: () => {},
    setStateCombiner: () => {},
});

export const useMapContext = () => React.useContext(MapContext);
export const useMapContextState = () => React.useContext(MapContext).state;
export const useUpdateMapContext = () => React.useContext(MapContext).update;

let logTimout: NodeJS.Timeout;

export const MapProvider: React.FC<any> = ({ children }) => {
    const isMobile = useIsMobile();
    const map = useViewState<MapContextState>({
        rooms: [],
        laizeProps: [],
        laizeResults: [],
        laizeDebugResults: [],
        type: RoomType.Sol,
    });

    useWindowLog(() => (window.MapState = map.getState));

    const stateCombiner = React.useRef<StateCombiner>();
    const setStateCombiner = (value: StateCombiner) => (stateCombiner.current = value);

    const zoomToFit = () => {
        const { rooms = [], type } = map.getState();
        ZoomUtils.zoomFitAllRooms(rooms.filter((x) => x.type === type));
    };

    const update = ({ values, mapModified, logs, afterUpdate }: UpdateMapParams) => {
        Logger.isLogEnabled() && console.group('MapProvider update');

        const oldState = map.getState();
        const nextState: Partial<MapContextState> = { ...oldState, ...values };
        const updatedValues = mapModified && stateCombiner.current ? stateCombiner.current.merge(nextState) : {};

        const mapState = { ...nextState, ...updatedValues, logs };
        // log with high latency
        clearTimeout(logTimout);
        logTimout = setTimeout(
            () => Logger.log('map will be update with', { mapState, values, mapModified, logs }),
            2000
        );

        map.update(mapState, afterUpdate);

        Logger.isLogEnabled() && console.groupEnd();
    };

    const setPanel = ({ values, logs, ...params }: UpdateMapParams) => {
        update({
            ...params,
            values: {
                ...ClearPanelState,
                ...values,
            },
            logs: { ...logs, setPanel: 'map updateBy setPanel' },
        });
    };

    const addRooms = ({ rooms = [], openRoomPanel, mapModified, logs, ...rest }: AddRoomsParams) => {
        const { rooms: oldRooms = [] } = map.getState();

        let updatedWalls = MapHelper.getWalls(oldRooms);
        let updatedRooms = oldRooms;
        let updatedOpenings = MapHelper.getOpenings(oldRooms);
        let updatedRoomItems = MapHelper.getRoomItems(oldRooms);

        rooms.forEach((baseRoom) => {
            const roomsToUse = updatedRooms.filter((x) => x.type === map.state.type);
            baseRoom.name = baseRoom.name || RoomFactory.defaultName(roomsToUse.length, map.state.type);
            baseRoom.type = map.state.type;
            let room = baseRoom;

            if ([RoomType.Sol, RoomType.Wall].includes(baseRoom.type!)) {
                const wallsToUse = roomsToUse.flatMap((x) => x.walls ?? []);
                const { room: createdRoom, roomWalls = [] } = RoomUtils.createRoom(baseRoom, wallsToUse);
                updatedWalls = [...updatedWalls, ...roomWalls];
                room = createdRoom;
                room.walls = roomWalls;
                room.flooringWallStart = roomWalls[0];
            }

            updatedRooms = [...updatedRooms, room];
            Editor.architect(updatedWalls, updatedRooms);

            //! fix opening position from location_position_from_edge (occurs when duplicate room or import room)
            OpeningUtils.fixPositionOpenings(room, room.openings, updatedWalls);

            updatedOpenings = [...updatedOpenings, ...(room.openings || [])];
            updatedRoomItems = [...updatedRoomItems, ...(room.roomItems || [])];
            WallUtils.refreshWallsMinimumSizeAndOriginalAngle(updatedWalls, updatedOpenings);

            room.openSections = { infos: openRoomPanel };
        });

        updatedRooms.forEach((room) => {
            if (CompleteHelper.isOnlyRoomQuestionsCopiedAnswerMising(room)) {
                room.questionsCopiedFromId = MapItemContants.QuestionsCopiedFromId_NONE;
            }
        });

        if (rooms.length) {
            const openRoomIdPanel = openRoomPanel ? rooms[0].roomId : undefined;
            update({
                values: {
                    rooms: updatedRooms,
                    ...ClearPanelState,
                    selectedRoomId: isMobile ? undefined : openRoomIdPanel,
                    ...rest,
                    panZoomDisabled: false,
                },
                mapModified,
                logs,
            });
            ZoomUtils.zoomFitAllRooms(updatedRooms.filter((x) => x.type === map.state.type));
            if (oldRooms.length === 0 && !map.state.addRoomTutorialShown && !isMobile) {
                map.update({ showTutorial: true, addRoomTutorialShown: true });
            }
        }
    };

    //#region //* COMPUTE LAIZE

    const withComputeLaize = (mapState: Partial<MapContextState>): Partial<MapContextState> => {
        const { rooms = [] } = mapState;
        if (!rooms.length) {
            return {};
        }
        const { walls, openings, roomItems } = MapHelper.getRoomAllElements(rooms);

        // SOL & WALL
        Editor.architect(walls, rooms);
        rooms
            .filter((r) => [RoomType.Sol, RoomType.Wall].includes(r.type!))
            .forEach((room) => {
                OpeningUtils.updateRoomOpenings(room, openings);
                RoomItemUtils.updateRoomItems(room, roomItems);
                RoomDiffUtils.roomComputeDifference(room, roomItems, walls);
            });
        WallUtils.refreshWallsMinimumSizeAndOriginalAngle(walls, openings);

        const newLaizeProps = ProductHelper.ToArrayLaizeProps(rooms);
        const { rooms: updatedRooms = [], ...rest } = MapResult.calculate({
            rooms,
            roomItems,
            walls,
            openings,
            laizeProps: newLaizeProps,
            logs: 'called withComputeLaize',
        });

        // STAIRS
        const stairs = updatedRooms.filter((r) => r.type === RoomType.Stairs);
        const newLaizeStairsProps = ProductHelper.ToArrayLaizeProps(stairs);

        return {
            rooms: updatedRooms,
            laizeProps: newLaizeProps,
            laizeStairsProps: newLaizeStairsProps,
            ...rest,
        };
    };

    //#endregion

    //Logger.log('MapContext', { state: map.state, mode: MapConstants.mode });

    return (
        <MapReadyProvider>
            <GestureProvider>
                <LaizeViewProvider>
                    <EditNameProvider>
                        <MapContext.Provider
                            value={{
                                ...map,
                                update,
                                setStateCombiner,
                                addRooms,
                                setPanel,
                                withComputeLaize,
                                zoomToFit,
                            }}>
                            <UndoRedoMapProvider>
                                <MapKeybordEventsWrapper>
                                    <MapExportProvider>{children}</MapExportProvider>
                                </MapKeybordEventsWrapper>
                            </UndoRedoMapProvider>
                        </MapContext.Provider>
                    </EditNameProvider>
                </LaizeViewProvider>
            </GestureProvider>
        </MapReadyProvider>
    );
};

export const useMapProvider = () => {
    const map = useMapContext();
    const undoMap = useUndoRedoMap();

    const deleteRoom = (roomId: string) => {
        const mapState = map.getState();
        undoMap.store(mapState);
        const { rooms = [], selectedRoomId, selectStairsOpen } = mapState;
        map.update({
            values: {
                rooms: rooms.filter((r) => r.roomId !== roomId),
                selectedRoomId: selectedRoomId === roomId ? undefined : selectedRoomId,
                selectStairsOpen: selectStairsOpen?.stairsId === roomId ? undefined : selectStairsOpen,
                busy: false,
            },
            mapModified: true,
            logs: { event: 'deleteRoom', roomId },
        });
    };

    const deleteOpening = (openingId: string) => {
        const mapState = map.getState();
        undoMap.store(mapState);
        const { rooms = [], selectedOpeningId } = mapState;
        for (const room of rooms) {
            const index = room.openings?.findIndex((x) => x.openingId === openingId);
            if (index !== undefined && index >= 0) {
                room.openings?.splice(index, 1);
            }
        }
        map.update({
            values: {
                rooms,
                selectedOpeningId: selectedOpeningId === openingId ? undefined : selectedOpeningId,
                busy: false,
            },
            mapModified: true,
            logs: { event: 'deleteOpening', openingId },
        });
    };

    const deleteRoomItem = (roomItemId: string) => {
        const mapState = map.getState();
        undoMap.store(mapState);
        const { rooms = [], selectedRoomItemId } = mapState;
        for (const room of rooms) {
            const index = room.roomItems?.findIndex((x) => x.roomItemId === roomItemId);
            if (index !== undefined && index >= 0) {
                room.roomItems?.splice(index, 1);
            }
        }
        map.update({
            values: {
                rooms,
                selectedRoomItemId: selectedRoomItemId === roomItemId ? undefined : selectedRoomItemId,
                busy: false,
            },
            mapModified: true,
            logs: { event: 'deleteRoomItem', roomItemId },
        });
    };

    const deleteWall = (wallToDelete: Wall) => {
        const mapState = map.getState();
        undoMap.store(mapState);
        const { rooms, openings, walls } = MapHelper.getRoomAllElements(mapState.rooms);
        const room = rooms.find((x) => x.roomId === wallToDelete.roomId);
        if (room) {
            WallUtils.deleteWall(room.walls, wallToDelete);
        }
        OpeningUtils.resetOpeningsPlacement(openings, walls);
        map.update({
            values: {
                panZoomDisabled: false,
                wallMove: undefined,
                wallMoveInit: undefined,
                busy: false,
            },
            mapModified: true,
            logs: { event: 'deleteWall' },
        });
        Editor.architect(walls, rooms);
    };

    const halfSplitWall = (wallToSplit: Wall) => {
        const mapState = map.getState();
        undoMap.store(mapState);
        const { rooms, openings, walls } = MapHelper.getRoomAllElements(mapState.rooms);
        const room = rooms.find((x) => x.roomId === wallToSplit.roomId);
        let newAllRooms: Array<IRoom>;
        if (room) {
            newAllRooms = WallUtils.splitWall(
                walls,
                rooms,
                wallToSplit,
                qSVG.middle(wallToSplit.start.x, wallToSplit.start.y, wallToSplit.end.x, wallToSplit.end.y)
            ).newAllRooms;
        } else {
            newAllRooms = rooms;
        }
        const newWalls = MapHelper.getWalls(newAllRooms);
        OpeningUtils.resetOpeningsPlacement(openings, newWalls);
        Editor.architect(newWalls, newAllRooms);
        map.update({
            values: {
                rooms: newAllRooms,
                panZoomDisabled: false,
                wallMove: undefined,
                wallMoveInit: undefined,
                busy: false,
            },
            mapModified: true,
            logs: { event: 'splitWall' },
        });
    };

    return { ...map, deleteRoom, deleteOpening, deleteRoomItem, deleteWall, halfSplitWall };
};
